// Vuex module backing the «morph» between the floating app-dock and a
// sticky save-bar. Pages opt in by committing `register` in onMounted
// (with their save/cancel handlers) and committing `setDirty(true)` when
// any local form field diverges from its initial value. App.vue renders
// the save-bar in the same visual slot as the dock whenever
// `showSaveBar` is true. See ProfileDetailsSettings.vue for the first
// consumer and the accompanying spec for the contract.

import type { Module } from 'vuex';

export interface FormAction {
    onSave: () => Promise<void> | void;
    onCancel?: () => void;
    saveLabel?: string;
    cancelLabel?: string;
}

interface State {
    dirty: boolean;
    saving: boolean;
    error: string | null;
    saveLabel: string;
    cancelLabel: string;
    onSave: (() => Promise<void> | void) | null;
    onCancel: (() => void) | null;
}

const state = (): State => ({
    dirty: false,
    saving: false,
    error: null,
    saveLabel: 'Сохранить изменения',
    cancelLabel: 'Отменить',
    onSave: null,
    onCancel: null,
});

const mutations = {
    register(state: State, payload: FormAction) {
        state.onSave = payload.onSave;
        state.onCancel = payload.onCancel ?? null;
        state.saveLabel = payload.saveLabel ?? 'Сохранить изменения';
        state.cancelLabel = payload.cancelLabel ?? 'Отменить';
    },
    unregister(state: State) {
        state.onSave = null;
        state.onCancel = null;
        state.dirty = false;
        state.saving = false;
        state.error = null;
    },
    setDirty(state: State, value: boolean) {
        state.dirty = value;
        if (!value) state.error = null;
    },
    setSaving(state: State, value: boolean) {
        state.saving = value;
    },
    setError(state: State, value: string | null) {
        state.error = value;
    },
};

const actions = {
    async triggerSave({ state, commit }: any) {
        if (!state.onSave || state.saving) return;
        commit('setSaving', true);
        commit('setError', null);
        try {
            await state.onSave();
            commit('setDirty', false);
            commit('setSaving', false);
        } catch (err: any) {
            commit('setSaving', false);
            commit('setError', err?.message || 'Не удалось сохранить');
            throw err;
        }
    },
    triggerCancel({ state, commit }: any) {
        if (state.onCancel) state.onCancel();
        commit('setDirty', false);
        commit('setError', null);
    },
};

const getters = {
    showSaveBar: (state: State) => state.dirty && !!state.onSave,
    saveBarState: (state: State) => ({ ...state }),
};

const formActionBar: Module<State, any> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default formActionBar;
